import React, { useState } from "react";

import { Tab, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { selectAuthToken } from "./../../store/selectors/AuthSelectors";
import ApiService from "./../../services/AxiosInstance";
import { useDegrees } from "./../../hooks/useDegrees";

import { months } from "./../utils";

const paymentTypes = [
  { id: "wompilink", name: "WOMPI" },
  { id: "CASH", name: "EFECTIVO" },
];
const paymentStatus = ["Pago", "No pago"];

const Report = () => {
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState((new Date().getMonth() + 1).toString());
  const [degreeFilter, setDegreeFilter] = useState("");
  const [statusPaymentFilter, setStatusPaymentFilter] = useState("");
  const [typePaymentFilter, setTypePaymentFilter] = useState("");

  const token = useSelector(selectAuthToken);
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingExcel, setIsFetchingExcel] = useState(false);

  const { degrees: datadegreeList } = useDegrees(yearFilter);

  const getParams = () => ({
    year: yearFilter,
    month: monthFilter,
    degree: degreeFilter,
    status_payment: statusPaymentFilter,
    payment_type: typePaymentFilter,
  });

  const fetchReport = async () => {
    setIsFetching(true);
    try {
      const response = await ApiService.get("/student/report-payments", {
        headers: { Authorization: `Bearer ${token}` },
        params: getParams(),
      });
      setData(response.data);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setData({ students: [] });
      } else {
        console.error("Error fetching report:", error);
        setData(null);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const fetchExcel = async () => {
    setIsFetchingExcel(true);
    const params = getParams();
    try {
      const response = await ApiService.get("/student/export-student-payments", {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        params: params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "reporte.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error exporting:", error);
    } finally {
      setIsFetchingExcel(false);
    }
  };

  return (
    <Tab.Container defaultActiveKey="all">
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          <select
            className="form-control mb-3"
            onChange={(e) => setYearFilter(e.target.value)}
            style={{ width: 100 }}
            value={yearFilter}
          >
            {[2025, 2026, 2027, 2028, 2029, 2030].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            className="form-control mb-3 ml-3"
            onChange={(e) => setDegreeFilter(e.target.value)}
            style={{ width: 200 }}
          >
            <option value="">Todos los grados</option>
            {datadegreeList?.map((e) => (
              <option key={e.id.toString()} value={e.id}>
                {e.level} {e.degree}
              </option>
            ))}
          </select>
          <select
            className="form-control mb-3 ml-3"
            onChange={(e) => setMonthFilter(e.target.value)}
            style={{ width: 150 }}
            value={monthFilter}
          >
            <option value="">Todos los meses</option>
            {months.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="form-control mb-3 ml-3"
            onChange={(e) => setTypePaymentFilter(e.target.value)}
            style={{ width: 150 }}
            value={typePaymentFilter}
          >
            <option value="">Todos</option>
            {paymentTypes?.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <select
            className="form-control mb-3 ml-3"
            onChange={(e) => setStatusPaymentFilter(e.target.value)}
            style={{ width: 150 }}
            value={statusPaymentFilter}
          >
            <option value="">Todos</option>
            <option value="1">Pagado</option>
            <option value="0">No Pagado</option>
          </select>
        </div>

        <div className="d-flex gap-5">
          <Button
            className="btn btn-primary text-nowrap mb-3 ml-3"
            onClick={fetchReport}
          >
            <i className="fa fa-search scale5 mr-3" aria-hidden="true" />
            Buscar
          </Button>
          <Button
            disabled={isFetchingExcel}
            className="btn btn-info text-nowrap mb-3 ml-3"
            onClick={fetchExcel}
          >
            {isFetchingExcel ? "Generando..." : "Generar Excel"}
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table card-table display dataTablesCard dataTable no-footer">
          <thead>
            <tr>
              <th>Identificación</th>
              <th>Nombre</th>
              <th>Grado</th>
              <th>Mes</th>
              <th>Medio de Pago</th>
              <th>Estado del Pago</th>
              <th>Fecha del Pago</th>
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <tr>
                <td colSpan={7} className="text-center">
                  Buscando...
                </td>
              </tr>
            ) : null}
            {data?.students?.length > 0 && !isFetching ? (
              data?.students?.map((row) => (
                <tr key={row.id}>
                  <td>{row.identification}</td>
                  <td>{row.nombres}</td>
                  <td>
                    {row.level} {row.degree}
                  </td>
                  <td>{months.find(m => m.id == row.month)?.name || '-'}</td>
                  <td>{row.payment_type === 'wompilink' ? 'WOMPI' : row.payment_type === 'CASH' ? 'EFECTIVO' : row.payment_type || '-'}</td>
                  <td>{row.estado_pago}</td>
                  <td>{row.payment_date || '-'}</td>
                </tr>
              ))
            ) : !isFetching ? (
              <tr>
                <td colSpan={7} className="text-center">
                  No hay datos disponibles
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Tab.Container>
  );
};

export default Report;
